import type { ComponentProps } from "react";
import { useRef, useState } from "react";
import { t } from "ttag";

import { RatingStars } from "@/assets/onboarding/rating-stars";
import { trackPricesSelected } from "@/features/analytics/trackers/prices-selected";
import { trackProductsSelected } from "@/features/analytics/trackers/products-selected";
import { CHECKOUT_ID } from "@/features/onboarding/components/choose-products/selected-product";
import { useOnboardingStore } from "@/features/onboarding/stores";
import type {
  AdministrationForm,
  Product,
  ProductGroup,
} from "@/features/products/models";
import type { Price } from "@/shared/domains";
import { ADMINISTRATION_FORM } from "@/shared/domains";
import {
  Badge,
  Heading,
  LAYOUT_SCROLL_VIEWPORT_ID,
  MarkdownContent,
  RadioGroup,
} from "@/shared/ui";
import { SegmentedControl } from "@/shared/ui/common/segmented-control";
import { formatPrice, tw } from "@/shared/utils";
import { scrollToSection } from "@/shared/utils/dom";

const getEffectivePrice = (price: Price) => {
  if (price.discount?.isValid) {
    return price.discount.priceAfterDiscount;
  }
  return price.price;
};

const priceToShow = (product: Product, priceId?: Price["id"]) => {
  const selectedPriceForThisProduct = product.prices.find(
    (price) => price.id === priceId,
  );

  if (selectedPriceForThisProduct) {
    return (
      getEffectivePrice(selectedPriceForThisProduct) /
      selectedPriceForThisProduct.durationInMonths
    );
  }

  const highestMonthlyPrice = product.prices[product.prices.length - 1];

  if (!highestMonthlyPrice) {
    return 0;
  }

  return (
    getEffectivePrice(highestMonthlyPrice) /
    highestMonthlyPrice.durationInMonths
  );
};

export const ProductGroupCard = ({ group }: { group: ProductGroup }) => {
  const [selectedForm, setSelectedForm] = useState<AdministrationForm>(
    ADMINISTRATION_FORM.INJECTABLE,
  );

  const selectedProduct = group.products.find(
    (product) => product.administrationForm === selectedForm,
  );

  const isSelected = useOnboardingStore((state) =>
    state.selectedCategories.some(
      (category) =>
        category.id === selectedProduct?.category?.id &&
        category.selectedProduct?.id === selectedProduct?.id,
    ),
  );

  const selectedPrice = useOnboardingStore(
    (state) =>
      state.selectedCategories.find(
        (category) => category.id === selectedProduct?.category?.id,
      )?.selectedProduct?.selectedPrice,
  );

  const setSelectedPrice = useOnboardingStore(
    (state) => state.setSelectedPrice,
  );
  const setCheckoutDiscount = useOnboardingStore(
    (state) => state.setCheckoutDiscount,
  );
  const addSelectedProduct = useOnboardingStore(
    (state) => state.addSelectedProduct,
  );

  const hasMultipleForms = group.products.length > 1;
  const lastTrackedProductIdRef = useRef<number | null>(null);
  const lastTrackedPriceIdRef = useRef<number | null>(null);

  if (!selectedProduct) {
    return null;
  }

  const handleProductSelect = () => {
    addSelectedProduct(selectedProduct);

    if (selectedProduct.id !== lastTrackedProductIdRef.current) {
      lastTrackedProductIdRef.current = selectedProduct.id;
      trackProductsSelected({
        selectedCategories: useOnboardingStore.getState().selectedCategories,
        userId: useOnboardingStore.getState().contactDetails?.email,
      });
    }

    if (selectedProduct.prices.length === 1) {
      const onlyPriceId = selectedProduct.prices[0]?.id;

      handlePriceChange(String(onlyPriceId));
    }
  };

  const handlePriceChange = (id: string) => {
    const price = selectedProduct.prices.find(
      (price) => price.id === Number(id),
    );

    if (price) {
      addSelectedProduct(selectedProduct);
      setSelectedPrice(selectedProduct.category.id, price);

      if (price.discount?.isValid) {
        setCheckoutDiscount({
          isValid: true,
          code: price.discount.code,
          prices: [price],
          total: price.price,
          totalDiscount: price.discount.amountOff,
          totalAfterDiscount: price.discount.priceAfterDiscount,
        });
      } else {
        setCheckoutDiscount(undefined);
      }

      scrollToSection(CHECKOUT_ID, LAYOUT_SCROLL_VIEWPORT_ID);

      if (price.id !== lastTrackedPriceIdRef.current) {
        lastTrackedPriceIdRef.current = price.id;
        trackPricesSelected({
          selectedCategories: useOnboardingStore.getState().selectedCategories,
          userId: useOnboardingStore.getState().contactDetails?.email,
        });
      }
    }
  };

  const handleAdministrationFormChange = (value: AdministrationForm) => {
    const newProduct = group.products.find(
      (product) => product.administrationForm === value,
    );

    if (newProduct && isSelected) {
      addSelectedProduct(newProduct);
    }

    setSelectedForm(value);
  };

  const formattedPrice = formatPrice(
    priceToShow(selectedProduct, selectedPrice?.id),
  );

  const minDurationInMonths = selectedProduct.prices[0]?.durationInMonths ?? 1;

  return (
    <div
      role="button"
      tabIndex={0}
      className={tw(
        "overflow-hidden rounded-2xl border border-secondary-200 text-start shadow-sm",
        isSelected && "border-secondary-500 bg-secondary-500",
      )}
      onClick={handleProductSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleProductSelect();
        }
      }}
    >
      <img
        alt={selectedProduct.name}
        src={selectedProduct.thumbnail ?? "#"}
        className="h-60 w-full object-cover object-center"
      />

      <div
        className={tw(
          "relative -mt-2 flex flex-col gap-4 rounded-t-lg border-t border-secondary-100 bg-white p-5 text-primary-950 transition-all duration-100",
          isSelected &&
            "rounded-b-2xl border-secondary-500 inset-ring-2 inset-ring-secondary-500",
        )}
      >
        <div className="flex flex-col gap-1">
          <div className="flex justify-between">
            <Heading level={3}>{group.name}</Heading>
            <RatingStars className="w-24" />
          </div>

          <div className="flex justify-between">
            <span className="font-medium">
              {t`Prescribed for only`}{" "}
              <span className="text-secondary-500">{t`${formattedPrice} /month`}</span>
            </span>

            {selectedPrice?.durationInMonths &&
              isSelected &&
              selectedPrice.durationInMonths > minDurationInMonths && (
                <p className="text-neutral-600 line-through">
                  {formatPrice(
                    (selectedProduct.retailPrice ?? 0) / minDurationInMonths,
                  )}
                  {t`/mo`}
                </p>
              )}
          </div>
        </div>

        <MarkdownContent>{selectedProduct.description}</MarkdownContent>

        {hasMultipleForms && (
          <>
            <hr className="h-px w-full border-secondary-200" />

            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div onClick={(e) => e.stopPropagation()}>
              <AdministrationFormSelector
                value={selectedForm}
                onChange={handleAdministrationFormChange}
              />
            </div>
          </>
        )}

        <PriceSelector
          value={selectedPrice ? String(selectedPrice.id) : undefined}
          prices={selectedProduct.prices}
          onValueChange={handlePriceChange}
          onClick={(e) => e.stopPropagation()}
        />

        {selectedProduct.features && (
          <div className="rounded-xl border border-secondary-200 bg-secondary-50 p-4 text-primary-900">
            <MarkdownContent>{selectedProduct.features}</MarkdownContent>
          </div>
        )}
      </div>
    </div>
  );
};

interface AdministrationFormSelectorProps {
  value: AdministrationForm;
  onChange: (form: AdministrationForm) => void;
}

const AdministrationFormSelector = ({
  value,
  onChange,
}: AdministrationFormSelectorProps) => (
  <div className="flex flex-col gap-2">
    <Heading level={3} align="center">{t`Which do you prefer?`}</Heading>

    <SegmentedControl
      value={value}
      onChange={onChange}
      options={[
        { label: t`Injection`, value: ADMINISTRATION_FORM.INJECTABLE },
        { label: t`Oral`, value: ADMINISTRATION_FORM.ORAL },
      ]}
    />
  </div>
);

type PriceSelectorProps = ComponentProps<typeof RadioGroup.Root> & {
  prices: Product["prices"];
};

const PriceSelector = ({ prices, ...props }: PriceSelectorProps) => (
  <div className="flex flex-col gap-2">
    <Heading level={3} align="center">
      {t`Choose your package`}:
    </Heading>

    <RadioGroup.Root isCard {...props}>
      {prices.map(({ id, price, durationInMonths, tags, discount }) => {
        const hasDiscount = discount?.isValid && discount.priceAfterDiscount;
        const effectivePrice = hasDiscount
          ? discount.priceAfterDiscount
          : price;
        const formattedPricePerMonth = formatPrice(
          effectivePrice / durationInMonths,
        );
        const formattedOriginalPrice = hasDiscount
          ? formatPrice(price / durationInMonths)
          : null;

        return (
          <RadioGroup.Item key={id} value={String(id)}>
            <div className="flex justify-between gap-2">
              <RadioGroup.Label className="font-bold">
                {formattedOriginalPrice && (
                  <span className="mr-1 text-xs font-normal text-neutral-600 line-through">
                    {formattedOriginalPrice}
                  </span>
                )}
                {formattedPricePerMonth}
                <span className="text-xs font-normal">
                  {durationInMonths > 1
                    ? t`/ ${durationInMonths} Months`
                    : t` / Monthly`}
                </span>
              </RadioGroup.Label>
              {tags.length > 0 && (
                <div className="flex items-center gap-1">
                  {tags.map((tag) => (
                    <Badge
                      key={tag.label}
                      size="x-small"
                      color="dark-secondary"
                      className="whitespace-nowrap"
                    >
                      {tag.label}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </RadioGroup.Item>
        );
      })}
    </RadioGroup.Root>
  </div>
);
